/**
 * The authoring side of the survey-wide countdown (spec 35).
 *
 * `surveyTimer.ts` is the RESPONDENT runtime — what a live session shows. This is the other half:
 * turning a researcher's form input into the `timer_config` the API stores, and reading a stored
 * one back into the form.
 *
 * Kept separate from `surveyTimer.ts` deliberately. That module is imported by the session runtime
 * and carries its heartbeat/nudge constants; nothing a respondent runs needs the authoring bounds,
 * and nothing here needs the heartbeat cadence.
 *
 * The bounds below MIRROR `constants/interview_constants.SURVEY_TIMER_CONFIG` on the backend. The
 * server is the authority — `utilities/survey_timer.timer_config_for` CLAMPS out-of-range values
 * rather than rejecting them, and the publish gate refuses activation separately. These exist so
 * the form can guide the author before a round trip, NOT so the client can decide what is valid.
 * If they ever disagree with the backend, the backend wins and the author sees a clamped value.
 */
import type { SurveyTimerConfig, SurveyTimerMode } from './surveyTimer';

/** Mirrors SURVEY_TIMER_CONFIG['DEFAULT_DURATION_SECONDS']. */
export const DEFAULT_DURATION_SECONDS = 30 * 60;
/** Mirrors SURVEY_TIMER_CONFIG['MIN_DURATION_SECONDS'] — a survey must outlast its final warning. */
export const MIN_DURATION_SECONDS = 120;
/** Mirrors SURVEY_TIMER_CONFIG['MAX_DURATION_SECONDS'] — past this the clock does no work. */
export const MAX_DURATION_SECONDS = 4 * 60 * 60;
/** Mirrors SURVEY_TIMER_CONFIG['DEFAULT_MODE']. */
export const DEFAULT_MODE: SurveyTimerMode = 'guidance';

const SECONDS_PER_MINUTE = 60;

export const MIN_DURATION_MINUTES = MIN_DURATION_SECONDS / SECONDS_PER_MINUTE;
export const MAX_DURATION_MINUTES = MAX_DURATION_SECONDS / SECONDS_PER_MINUTE;
export const DEFAULT_DURATION_MINUTES = DEFAULT_DURATION_SECONDS / SECONDS_PER_MINUTE;

/** The form's own shape: minutes as a string, because it is bound to a text input. */
export interface TimerFormState {
  enabled: boolean;
  durationMinutes: string;
  mode: SurveyTimerMode;
}

export const EMPTY_TIMER_FORM: TimerFormState = {
  enabled: false,
  durationMinutes: String(DEFAULT_DURATION_MINUTES),
  mode: DEFAULT_MODE,
};

/**
 * Read a stored `timer_config` back into form state.
 *
 * A survey with no timer, or a malformed one, opens the form at its defaults with the toggle OFF —
 * the same fail-closed direction the backend takes. It must never open showing a clock the survey
 * is not actually on.
 */
export function timerFormFromConfig(config: SurveyTimerConfig | null | undefined): TimerFormState {
  if (!config || typeof config !== 'object' || !config.enabled) {
    return { ...EMPTY_TIMER_FORM };
  }
  const seconds = Number(config.duration_seconds);
  const minutes =
    Number.isFinite(seconds) && seconds > 0
      ? Math.round(seconds / SECONDS_PER_MINUTE)
      : DEFAULT_DURATION_MINUTES;
  return {
    enabled: true,
    durationMinutes: String(minutes),
    mode: config.mode === 'hard_stop' ? 'hard_stop' : DEFAULT_MODE,
  };
}

/**
 * Turn form state into the payload field.
 *
 * Returns `null` when the timer is off, which is what CLEARS a previously-set timer — the API
 * treats an explicit null as "remove" and an omitted field as "leave alone", so the caller must
 * send this value rather than dropping the key.
 *
 * `visible` is deliberately not sent. The backend defaults it to true, and the only case where
 * false is legal is a guidance timer the author wants hidden — a control the core form does not
 * offer, and a hidden hard stop is refused at publish anyway.
 */
export function timerConfigFromForm(form: TimerFormState): SurveyTimerConfig | null {
  if (!form.enabled) return null;
  return {
    enabled: true,
    duration_seconds: durationSecondsFromMinutes(form.durationMinutes),
    mode: form.mode,
    visible: true,
  };
}

/**
 * Minutes-as-typed to a clamped second count.
 *
 * Blank or unparseable falls back to the default rather than to zero: an author who clears the box
 * while the toggle is on has expressed no duration, not a zero-length survey.
 */
export function durationSecondsFromMinutes(minutes: string): number {
  const parsed = Number.parseInt(String(minutes).trim(), 10);
  if (!Number.isFinite(parsed)) return DEFAULT_DURATION_SECONDS;
  return clampDurationSeconds(parsed * SECONDS_PER_MINUTE);
}

export function clampDurationSeconds(seconds: number): number {
  return Math.min(Math.max(seconds, MIN_DURATION_SECONDS), MAX_DURATION_SECONDS);
}

/**
 * Why the author's current input would not be stored as typed — or null when it would.
 *
 * Advisory only. The backend clamps rather than rejects, so this exists to tell the author what
 * the server is about to do, not to block a save.
 */
export function durationWarning(form: TimerFormState): string | null {
  if (!form.enabled) return null;
  const raw = String(form.durationMinutes).trim();
  if (raw === '') return null;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return null;
  if (parsed < MIN_DURATION_MINUTES) {
    return `Minimum is ${MIN_DURATION_MINUTES} minutes — shorter will be saved as ${MIN_DURATION_MINUTES}.`;
  }
  if (parsed > MAX_DURATION_MINUTES) {
    return `Maximum is ${MAX_DURATION_MINUTES} minutes — longer will be saved as ${MAX_DURATION_MINUTES}.`;
  }
  return null;
}
